import { hashKey } from "@tanstack/react-query";
import type { HistoryFilter } from "@common/types/auditLog";
import type {
    DensityChartHighlight,
    DensityChartWindow,
} from "@design-system/DensityChart/DensityChart.lib";
import type { ActorPickFilter } from "./activityTimelineNarrowedCounts.lib";
import { toActorPicks } from "./activityTimelineNarrowedCounts.lib";
import type {
    ActivityTimelineSeries,
    TimelineSeriesKey,
} from "./activityTimelineSeries.lib";

/** Whether Updated by picks something finer than a whole series, like a person, one integration or Kick Support. */
const hasFinerPicks = ({
    actorIds,
    actorType,
    sourceTypes,
    sourcePredicates,
}: HistoryFilter) =>
    (actorIds?.length ?? 0) > 0 ||
    actorType !== undefined ||
    (sourceTypes?.length ?? 0) > 0 ||
    (sourcePredicates?.length ?? 0) > 0;

/**
 * The counts' filter: the list's, less the whole series Updated by picks
 * when it picks nothing finer, since the chart then draws them selected
 * among every series instead. Finer picks narrow the counts to all of
 * Updated by.
 */
export const toTimelineCountsFilter = <Filter extends HistoryFilter>(
    filter: Filter,
): Filter =>
    hasFinerPicks(filter) ? filter : { ...filter, series: undefined };

/**
 * The segments drawn selected, in the bars sharing a day with `range`: those
 * of the whole series Updated by picks, and those a click would leave
 * Updated by unchanged for, like a person, Kick Support or one integration
 * picked alone. A click on a selected segment clears Updated by.
 */
export function toTimelineHighlight({
    picks,
    range,
    series,
    toClickPicks,
}: {
    picks: ActorPickFilter;
    range: DensityChartWindow;
    series: readonly ActivityTimelineSeries[];
    /** The Updated by a click on the series sets. */
    toClickPicks: (key: TimelineSeriesKey) => ActorPickFilter;
}): DensityChartHighlight | undefined {
    const currentPicks = hashKey([toActorPicks(picks)]);
    const seriesIds = series.flatMap((chartSeries) => {
        const isPicked =
            picks.series?.includes(chartSeries.series) ||
            hashKey([toActorPicks(toClickPicks(chartSeries))]) === currentPicks;
        return isPicked ? [chartSeries.id] : [];
    });
    return seriesIds.length > 0
        ? { range, seriesIds: new Set(seriesIds) }
        : undefined;
}
